import type { ReactionInstance } from './ReactionInstance';

// Глобальный реестр всех запущенных реакций
const runningReactions = new Set<Promise<unknown>>();

// Глобальный реестр всех экземпляров реакций
const allReactionsInstances: Set<Map<unknown, ReactionInstance<unknown, unknown>>> = new Set();

// Отслеживание, ожидаются ли реакции (для тестирования)
let isReactionsWaited: boolean = false;

/**
 * Добавить промис реакции в реестр запущенных реакций
 */
export function addRunningReactionToRegistry(reactionPromise: Promise<void>): Promise<void> {
    runningReactions.add(reactionPromise);
    return reactionPromise.finally(() => {
        runningReactions.delete(reactionPromise);
    });
}

/**
 * Дождаться завершения всех запущенных реакций (с ограничением рекурсии)
 */
export async function waitAllRunningReactions(limit = 5): Promise<void> {
    isReactionsWaited = true;
    await Promise.allSettled(runningReactions);

    if (limit >= 0 && runningReactions.size > 0) {
        await waitAllRunningReactions(limit - 1);
    }
}

function getAndResetIsReactionsWaited(): boolean {
    if (runningReactions.size > 0) {
        const result = isReactionsWaited;
        isReactionsWaited = false;
        return result;
    }

    isReactionsWaited = false;
    return true;
}

/**
 * Проверить, что все реакции были ожидаемы (утилита для тестирования)
 */
export function assertIsReactionsWaited(): void {
    const message =
        // eslint-disable-next-line @adv-frontend/config/no-rus-string-locales-rule -- сообщение об ошибке только для тестов
        'В тесте присутствуют запущенные реакции. Добавьте ожидание выполнения реакций "waitAllRunningReactions", чтобы исключить нестабильность теста.';
    if (!getAndResetIsReactionsWaited()) {
        throw new Error(message);
    }
}

/**
 * Очистить все реакции - останавливает подписки и удаляет экземпляры
 */
export function cleanupReactions(): void {
    for (const reactionInstances of allReactionsInstances) {
        for (const reactionInstance of reactionInstances.values()) {
            reactionInstance.destroy();
            reactionInstance.cancelActiveRuns();
            reactionInstances.delete(reactionInstance.param);
        }
    }
}

/**
 * Зарегистрировать карту экземпляров в глобальном реестре
 */
export function registerInstancesMap<R, P>(instances: Map<P, ReactionInstance<R, P>>): void {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- тип параметров не важен в кеше
    allReactionsInstances.add(instances as Map<unknown, ReactionInstance<unknown, unknown>>);
}
